import { OpDrawer } from "components/customAntd/DLS/OpDrawer/OpDrawer";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpTabs } from "components/customAntd/DLS/OpTabs/OpTabs";
import { MultiFormButtons } from "components/customAntd/MultiFormButtons";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import { Spin, notification } from "antd";
import { useConfirmModal } from "utils/customHooks/useConfirmModal"
import { DRAWER_WIDTH } from "constants/ui";
import { clearSelectedVisitorWorkflow } from "store/slices/visitorWorkflowsSlice";
import WorkflowContent from "./tabs/WorkflowContent";
import FieldsContent from "./tabs/FieldsContent";
import AgreementsContent from "./tabs/AgreementsContent";
import { LoadingOutlined } from '@ant-design/icons';
import { hasPermission } from "utils/utils";
import { logUserActivity } from "store/slices/userActivitySlice";
import { useTranslation } from "react-i18next";

interface AttendantSignInDrawerProps {
    open: boolean;
    onClose: () => void;
}

const AttendantSignInDrawer: React.FC<AttendantSignInDrawerProps> = ({ open, onClose }) => {
    const dispatch: AppDispatch = useDispatch();
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisitorWorkflow, describeVisitorWorkflowLoading } = useSelector((state: RootState) => state.visitorWorkflows);

    const [activeKey, setActiveKey] = useState<string>('workflow');

    const [workflowForm] = OpForm.useForm();
    const [fieldsForm] = OpForm.useForm();
    const [agreementsForm] = OpForm.useForm();

    const confirmModal = useConfirmModal();
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const hasSigninconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:w');

    const { t } = useTranslation();

    useEffect(() => {
        if (open) {
            setActiveKey('workflow');
            // Reset forms whenever the drawer opens to ensure they start fresh
            workflowForm.resetFields();
            fieldsForm.resetFields();
            agreementsForm.resetFields();
        }
    }, [open, agreementsForm, workflowForm, fieldsForm]);

    // Called when the save button is clicked
    const handleSubmitAllForms = async () => {
        try {
            const workflowValues = await workflowForm.validateFields();
            const fieldsValues = await fieldsForm.validateFields();
            const agreementsValues = await agreementsForm.validateFields();

            if (workflowValues && fieldsValues && agreementsValues) {
                const submitPromises = [];

                if (workflowForm.isFieldsTouched()) {
                    submitPromises.push(workflowForm.submit());
                }
                if (fieldsForm.isFieldsTouched()) {
                    submitPromises.push(fieldsForm.submit());
                }
                if (agreementsForm.isFieldsTouched()) {
                    submitPromises.push(agreementsForm.submit());
                }

                // Wait for all submits to complete
                await Promise.all(submitPromises);

                if (workflowForm.isFieldsTouched() || fieldsForm.isFieldsTouched() || agreementsForm.isFieldsTouched()) {
                    dispatch(logUserActivity({ orgId, userActivity: { userId: globalUserId!, activityId: "75", details: workflowForm.getFieldsValue().name || '' } }))
                }

                // Show notification and close drawer only after all operations complete
                notification.success({
                    message: t('notification.success'),
                    description: t('workflow.notification.updateSuccess'),
                    placement: 'bottomRight',
                });
                onClose();
            }
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('workflow.notification.saveError'),
                placement: 'bottomRight',
            });
        }
    };

    // Called when we close the drawer by clicking cancel button or touching the main page
    const finalOnClose = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (workflowForm.isFieldsTouched() || fieldsForm.isFieldsTouched() || agreementsForm.isFieldsTouched()) {
            confirmModal({
                title: t('confirmDiscard.title'),
                content: t('confirmDiscard.message'),
                okText: t('confirmDiscard.ok'),
                cancelText: t('confirmDiscard.cancel'),
                onOk: () => {
                    workflowForm.resetFields();
                    fieldsForm.resetFields();
                    agreementsForm.resetFields();
                    dispatch(clearSelectedVisitorWorkflow());
                    onClose();
                },
            });
        } else {
            dispatch(clearSelectedVisitorWorkflow());
            onClose();
        }
    };

    const tabItems = [
        {
            key: 'workflow',
            label: t('workflow.attendantSignIn.tabs.workflow'),
            children: <WorkflowContent form={workflowForm} />,
        },
        {
            key: 'fields',
            label: t('workflow.attendantSignIn.tabs.fields'),
            children: <FieldsContent form={fieldsForm} />,
        },
        {
            key: 'agreements',
            label: t('workflow.attendantSignIn.tabs.agreements'),
            children: <AgreementsContent form={agreementsForm} />,
        },
    ];

    return (
        <OpDrawer
            title={describeVisitorWorkflowLoading ? undefined : (selectedVisitorWorkflow ? `${selectedVisitorWorkflow.name}` : t('workflow.attendantSignIn.addTitle'))}
            width={DRAWER_WIDTH}
            open={open}
            onClose={finalOnClose}
            footer={
                !describeVisitorWorkflowLoading && hasSigninconfWrite && (
                    <MultiFormButtons
                        forms={[workflowForm, fieldsForm, agreementsForm]}
                        onSubmitClick={handleSubmitAllForms}
                        isSubmitButtonLoading={describeVisitorWorkflowLoading}
                    />
                )
            }
        >
            {describeVisitorWorkflowLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
            ) : (
                <OpTabs
                    activeKey={activeKey}
                    onChange={(newActiveKey: string) => {
                        setActiveKey(newActiveKey);
                    }}
                    items={tabItems}
                />
            )}
        </OpDrawer>
    );
};

export default AttendantSignInDrawer;